import { Avatar } from "antd";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { CameraIcon, MicroPhoneIcon, NetworkQualityIcon } from "../SvgIconCom";
import classNames from "classnames";
import "./index.less";
import { OverlayShowDirection, OverlayStyleType } from "@obsbot/remo-stores";

export interface OverlayAvatarComProps {
    isSpeak: boolean;
    avatarUrl: string;
    networkQuality: number;
    showAnimations: string[];
    isTurnOnMic: boolean;
    isTurnOnCamera: boolean;
    isShowAnimations: boolean;
    className?: string;
    size?: number;
    overlayShowStyle?: OverlayStyleType;
    overlayShowDirection?: OverlayShowDirection;
    isSettingPage?: boolean;
    onSelectedAnimations?: (animation: string) => void;
    onDoubleClickAnimations?: (animation: string) => void;
    onUserCameraOff?: () => void;
}

enum StatusType {
    None,
    Mic,
    Camera,
}

export const OverlayAvatarCom: React.FC<OverlayAvatarComProps> = forwardRef<
    {
        container: HTMLDivElement | null;
    },
    OverlayAvatarComProps
>(
    (
        {
            isSpeak,
            avatarUrl,
            networkQuality,
            isTurnOnMic,
            isTurnOnCamera,
            isShowAnimations,
            showAnimations,
            className = "",
            size = 50,
            overlayShowStyle = OverlayStyleType.Tab,
            overlayShowDirection = OverlayShowDirection.Vertical,
            isSettingPage,
            onSelectedAnimations,
            onDoubleClickAnimations,
            onUserCameraOff,
        },
        ref,
    ) => {
        const clickTimer = useRef<NodeJS.Timeout | null>(null);
        const statusChangeRef = useRef<HTMLDivElement | null>(null);
        const statusRestartTimer = useRef<number | null>(null);
        const [showStatus, setShowStatus] = useState<StatusType>(StatusType.None);
        const countSize = useMemo(() => {
            if (size <= 50) {
                // 10% ~ 50% 映射到 32 ~ 40px
                const p = (size - 10) / (50 - 10); // 0 ~ 1
                return 32 + p * (40 - 32);
            } else {
                // 50% ~ 100% 映射到 40 ~ 74px
                const p = (size - 50) / (100 - 50); // 0 ~ 1
                return 40 + p * (74 - 40);
            }
        }, [size]);
        const avatarContainerEl = useRef<HTMLDivElement | null>(null);
        useImperativeHandle(ref, () => ({
            container: avatarContainerEl.current,
        }));
        // 偏移大小, 为了处理特效
        const offsetSizeHeight = useMemo(() => {
            if (isShowAnimations) {
                if (overlayShowStyle === OverlayStyleType.Default) {
                    return countSize - 10;
                } else {
                    return 30;
                }
            } else {
                return overlayShowDirection === OverlayShowDirection.Vertical ? 10 : 30;
            }
        }, [isShowAnimations, countSize, overlayShowStyle, overlayShowDirection]);
        const offsetSizeWidth = useMemo(() => {
            if (isShowAnimations) {
                if (overlayShowStyle === OverlayStyleType.Default) {
                    return 40 + countSize + 10;
                } else {
                    return countSize + 80;
                }
            } else {
                return overlayShowDirection === OverlayShowDirection.Vertical ? 40 : 10;
            }
        }, [isShowAnimations, overlayShowStyle, overlayShowDirection, countSize]);
        const isHorizontalTab = useMemo(() => {
            return (
                isShowAnimations &&
                overlayShowDirection === OverlayShowDirection.Horizontal &&
                overlayShowStyle === OverlayStyleType.Tab
            );
        }, [isShowAnimations, overlayShowDirection, overlayShowStyle]);

        const triggerStatus = (status: StatusType): void => {
            // 清理可能存在的定时器
            if (statusRestartTimer.current) {
                window.clearTimeout(statusRestartTimer.current);
                statusRestartTimer.current = null;
            }
            // 先清理相关状态，确保后续能重新触发动画
            setShowStatus(StatusType.None);

            // 在下一帧强制重置动画样式（强制回流），然后短延时后设置目标状态
            requestAnimationFrame(() => {
                const el = statusChangeRef.current;
                if (el) {
                    // 通过直接操作 style 强制停止动画并触发回流
                    el.style.animation = "none";
                    // 读取 offsetWidth 强制回流
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-unused-expressions
                    el.offsetWidth;
                    el.style.animation = "";
                }
                statusRestartTimer.current = window.setTimeout(() => {
                    setShowStatus(status);
                    statusRestartTimer.current = null;
                }, 20);
            });
        };

        useEffect(() => {
            if (!isTurnOnCamera) {
                onUserCameraOff?.();
            }
        }, [isTurnOnCamera, onUserCameraOff]);

        useEffect(() => {
            triggerStatus(StatusType.Mic);
        }, [isTurnOnMic]);

        useEffect(() => {
            triggerStatus(StatusType.Camera);
        }, [isTurnOnCamera]);

        useEffect(() => {
            setShowStatus(StatusType.None);
        }, []);

        // 清理定时器
        useEffect(() => {
            return () => {
                if (statusRestartTimer.current) {
                    window.clearTimeout(statusRestartTimer.current);
                    statusRestartTimer.current = null;
                }
            };
        }, []);

        return (
            <div
                key={className}
                className={"OverlayAvatarCom " + className}
                style={{
                    width: countSize + offsetSizeWidth,
                    height: countSize + offsetSizeHeight,
                    justifyContent: isHorizontalTab ? "flex-start" : "center",
                }}
                tabIndex={-1}
            >
                <div
                    ref={avatarContainerEl}
                    className={classNames("overlay-camera", {
                        "overlay-speak": isSpeak,
                    })}
                    style={{ zIndex: 1002, width: countSize, height: countSize }}
                >
                    <canvas
                        className="overlay-video"
                        style={{ opacity: isTurnOnCamera ? 1 : 0 }}
                    ></canvas>
                    <div className="overlay-special"></div>
                    <div className="overlay-network">
                        <NetworkQualityIcon
                            className="network-quality-icon"
                            networkQuality={networkQuality}
                        />
                    </div>
                    <div
                        ref={statusChangeRef}
                        className={classNames("overlay-status-change", {
                            "overlay-status-change-show":
                                !isSettingPage && showStatus !== StatusType.None,
                        })}
                        onAnimationEnd={() => setShowStatus(StatusType.None)}
                    >
                        {showStatus === StatusType.Mic ? (
                            <MicroPhoneIcon active={isTurnOnMic} size={20} />
                        ) : (
                            <CameraIcon active={isTurnOnCamera} size={20} />
                        )}
                    </div>
                </div>
                <Avatar.Group
                    rootClassName={"overlay-avatar-group" + (isSpeak ? " overlay-speak" : "")}
                    style={{ width: countSize, height: countSize }}
                >
                    <Avatar shape="square" size={countSize - 8} src={avatarUrl} />
                    <div
                        className={"overlay-animations " + overlayShowStyle}
                        style={{
                            opacity: isShowAnimations ? 1 : 0,
                            width:
                                overlayShowStyle === OverlayStyleType.Default
                                    ? "100%"
                                    : countSize * 0.6 * 3 + 30 + countSize / 2,
                            marginLeft:
                                overlayShowStyle === OverlayStyleType.Default ? 0 : countSize / 2,
                            height:
                                overlayShowStyle === OverlayStyleType.Default
                                    ? "100%"
                                    : countSize * 0.91 - 8,
                            left:
                                overlayShowStyle === OverlayStyleType.Default ? 0 : -countSize / 2,
                        }}
                    >
                        {showAnimations.length !== 0 &&
                            showAnimations.map((item, index) => (
                                <div
                                    key={index}
                                    className="overlay-animation-item"
                                    style={{
                                        width: countSize * 0.6,
                                        height: countSize * 0.6,
                                    }}
                                    onClick={() => {
                                        if (clickTimer.current) {
                                            clearTimeout(clickTimer.current);
                                        }
                                        clickTimer.current = setTimeout(
                                            () => onSelectedAnimations?.(item),
                                            200,
                                        );
                                    }}
                                    onDoubleClick={() => {
                                        if (clickTimer.current) {
                                            clearTimeout(clickTimer.current);
                                            clickTimer.current = null;
                                        }
                                        onDoubleClickAnimations?.(item);
                                    }}
                                >
                                    <img src={item} />
                                </div>
                            ))}
                    </div>
                </Avatar.Group>
            </div>
        );
    },
);

OverlayAvatarCom.displayName = "OverlayAvatarCom";
